import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useDemoScenarios } from "@/hooks/useDemoScenarios";
import { ClinicalCodeSelector } from "./ClinicalCodeSelector";
import type { ClinicalCodeWithOrder } from "@/types/demo";
import { Loader2 } from "lucide-react";
import { log } from "@/utils/debug";

export function DemoScenarioForm() {
  const [demoText, setDemoText] = useState("");
  const [selectedCodes, setSelectedCodes] = useState<ClinicalCodeWithOrder[]>([]);
  const createScenario = useDemoScenarios().createScenario;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!demoText.trim()) {
      toast.error("Please enter demo text");
      return;
    }

    if (selectedCodes.length === 0) {
      toast.error("Please select at least one clinical code");
      return;
    }

    setIsSubmitting(true);
    try {
      // Use the raw text directly
      log.process("Creating demo scenario with raw text:", {
        textLength: demoText.length,
        lineCount: demoText.split("\n").length,
        textSample: demoText.substring(0, 50) + (demoText.length > 50 ? "..." : ""),
      });

      await createScenario.mutateAsync({
        demo_text: demoText, // Use raw text
        clinical_codes: selectedCodes as unknown as any,
      });

      setDemoText("");
      setSelectedCodes([]);
      toast.success("Demo scenario created successfully");
    } catch (_error) {
      toast.error("Failed to create demo scenario");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="demoText" className="text-sm font-medium">
          Demo Text
        </label>
        <Textarea
          id="demoText"
          value={demoText}
          onChange={(e) => setDemoText(e.target.value)}
          placeholder="Enter the demo text..."
          className="min-h-[160px]"
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Clinical Codes</label>
        <ClinicalCodeSelector value={selectedCodes} onChange={setSelectedCodes} disabled={isSubmitting} />
      </div>

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creating...
          </>
        ) : (
          "Create Demo Scenario"
        )}
      </Button>
    </form>
  );
}
